import React, { useState } from "react";
import { LifeBuoy, Send, AlertCircle, Tag } from "lucide-react";
import { realAPI, SupportTicketDTO } from "../../services/realAPI";
import { LoginUserData } from "../LoginForm/LoginForm";

interface SupportTicketsProps {
  currentUser: LoginUserData;
}

type SupportType =
  | "TECHNICAL"
  | "BILLING"
  | "ACCOUNT"
  | "FEATURE_REQUEST"
  | "BUG"
  | "GENERAL";

const SUPPORT_TYPES: { value: SupportType; label: string }[] = [
  { value: "TECHNICAL", label: "Technical" },
  { value: "BILLING", label: "Billing" },
  { value: "ACCOUNT", label: "Account" },
  { value: "FEATURE_REQUEST", label: "Feature Request" },
  { value: "BUG", label: "Bug" },
  { value: "GENERAL", label: "General" },
];

const SupportTickets: React.FC<SupportTicketsProps> = ({ currentUser }) => {
  const [subject, setSubject] = useState("");
  const [supportType, setSupportType] = useState<SupportType>("GENERAL");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // My tickets state
  const [myTickets, setMyTickets] = useState<SupportTicketDTO[]>([]);
  const [myPage, setMyPage] = useState(0);
  const [mySize] = useState(10);
  const [myTotalPages, setMyTotalPages] = useState(0);
  const [loadingMy, setLoadingMy] = useState(false);
  const [myError, setMyError] = useState<string | null>(null);

  // Ticket detail & comments
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketDTO | null>(
    null
  );
  const [ticketComments, setTicketComments] = useState<
    SupportTicketDTO["comments"]
  >([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Scroll target for details section
  const detailsRef = React.useRef<HTMLDivElement | null>(null);

  // When a ticket is selected, scroll the details into view smoothly
  React.useEffect(() => {
    if (selectedTicket && detailsRef.current) {
      // Smoothly bring details into view
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      // Also set focus for accessibility and visible focus ring if needed
      setTimeout(
        () =>
          detailsRef.current &&
          detailsRef.current.focus &&
          detailsRef.current.focus(),
        250
      );
    }
  }, [selectedTicket]);

  const loadMyTickets = async (page = 0) => {
    setLoadingMy(true);
    setMyError(null);
    try {
      const resp = await realAPI.getMySupportTickets(page, mySize);
      const data: SupportTicketDTO[] = resp?.data || [];
      setMyTickets(data);
      setMyPage(resp?.page ?? page);
      setMyTotalPages(resp?.totalPages ?? 0);
    } catch (e: any) {
      setMyError(e?.message || "Failed to load tickets");
    } finally {
      setLoadingMy(false);
    }
  };

  React.useEffect(() => {
    loadMyTickets(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!subject.trim()) {
      setErrorMsg("Subject is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        subject: subject.trim(),
        description: description.trim() || undefined,
        supportType,
        // pass org if known from current user
        organizationId: currentUser.organization?.id?.toString?.(),
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => !!t),
      };

      const resp = await realAPI.createSupportTicketRegistered(payload);

      // API may return { success, message, data }
      const ok = resp?.success !== false;
      const ticket = resp?.data || resp;
      const ticketNumber = ticket?.ticketNumber || ticket?.data?.ticketNumber;

      setSuccessMsg(
        ok
          ? `Support ticket created successfully${
              ticketNumber ? ` (#${ticketNumber})` : ""
            }`
          : "Ticket created"
      );

      // reset form
      setSubject("");
      setDescription("");
      setSupportType("GENERAL");
      setTags("");
      // refresh my tickets after creation
      loadMyTickets(0);
    } catch (err: any) {
      const msg = err?.message || "Failed to create ticket";
      setErrorMsg(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <LifeBuoy className="w-6 h-6 text-blue-600 mr-2" /> Support
        </h2>
        <p className="text-gray-600 mt-1">
          Create a support ticket for issues, questions, or feature requests.
        </p>
      </div>

      {errorMsg && (
        <div className="mb-4 p-3 rounded-md border border-red-200 bg-red-50 text-red-700 flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {successMsg && (
        <div className="mb-4 p-3 rounded-md border border-green-200 bg-green-50 text-green-700">
          {successMsg}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow p-6 space-y-5"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief summary of your request"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Support Type <span className="text-red-500">*</span>
          </label>
          <select
            value={supportType}
            onChange={(e) => setSupportType(e.target.value as SupportType)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            disabled={isSubmitting}
          >
            {SUPPORT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Describe the issue or request in detail..."
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Tag className="w-4 h-4 mr-1" /> Tags (comma-separated)
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. ui, login, payment"
            disabled={isSubmitting}
          />
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            <Send className="w-4 h-4 mr-2" />
            {isSubmitting ? "Submitting..." : "Submit Ticket"}
          </button>
        </div>
      </form>

      <div className="mt-6 text-sm text-gray-500">
        Logged in as {currentUser.firstName} {currentUser.lastName} (
        {currentUser.email})
      </div>

      {/* My Tickets List */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">My Tickets</h3>
          <button
            onClick={() => loadMyTickets(myPage)}
            className="text-sm text-blue-600 hover:underline"
            disabled={loadingMy}
          >
            {loadingMy ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {myError && (
          <div className="mb-3 p-3 rounded-md border border-red-200 bg-red-50 text-red-700">
            {myError}
          </div>
        )}

        {!loadingMy && myTickets.length === 0 && !myError && (
          <div className="text-gray-500 text-sm">No tickets yet.</div>
        )}

        <ul className="divide-y divide-gray-200 bg-white rounded-lg shadow">
          {myTickets.map((t) => (
            <li
              key={t.id}
              className="p-4 flex items-start justify-between hover:bg-gray-50 cursor-pointer"
              onClick={async () => {
                setSelectedTicket(null);
                setTicketComments([]);
                setLoadingDetails(true);
                setDetailsError(null);
                try {
                  const detailResp = await realAPI.getMyTicketById(t.id);
                  const ticket = detailResp?.data || t;
                  setSelectedTicket(ticket);
                  const commentsResp = await realAPI.getMyTicketComments(t.id);
                  setTicketComments(commentsResp?.data || []);
                } catch (e: any) {
                  setDetailsError(
                    e?.message || "Failed to load ticket details"
                  );
                } finally {
                  setLoadingDetails(false);
                }
              }}
            >
              <div className="pr-3">
                <div className="font-medium text-gray-900">
                  {t.ticketNumber ? `#${t.ticketNumber} - ` : ""}
                  {t.subject}
                </div>
                <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {t.description}
                </div>
                <div className="text-xs text-gray-500 mt-1 space-x-2">
                  <span>Priority: {t.priority}</span>
                  <span>Status: {t.status}</span>
                  {t.category && <span>Category: {t.category}</span>}
                </div>
              </div>
              <div className="text-xs text-gray-500 ml-4 whitespace-nowrap">
                {t.createdAt && new Date(t.createdAt).toLocaleString()}
              </div>
            </li>
          ))}
        </ul>

        {/* Pagination */}
        {myTotalPages > 1 && (
          <div className="flex items-center justify-between mt-3">
            <button
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              onClick={() => myPage > 0 && loadMyTickets(myPage - 1)}
              disabled={loadingMy || myPage === 0}
            >
              Previous
            </button>
            <div className="text-sm text-gray-600">
              Page {myPage + 1} of {myTotalPages}
            </div>
            <button
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              onClick={() =>
                myPage + 1 < myTotalPages && loadMyTickets(myPage + 1)
              }
              disabled={loadingMy || myPage + 1 >= myTotalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Ticket details & comments drawer */}
      {selectedTicket && (
        <div
          ref={detailsRef}
          tabIndex={-1}
          className="mt-8 bg-white rounded-lg shadow p-6 focus:outline-none"
        >
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">
                {selectedTicket.ticketNumber
                  ? `#${selectedTicket.ticketNumber} - `
                  : ""}
                {selectedTicket.subject}
              </h4>
              <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                {selectedTicket.description}
              </div>
              <div className="text-xs text-gray-500 mt-2 space-x-2">
                <span>Priority: {selectedTicket.priority}</span>
                <span>Status: {selectedTicket.status}</span>
                {selectedTicket.category && (
                  <span>Category: {selectedTicket.category}</span>
                )}
              </div>
            </div>
            <button
              className="text-sm text-gray-500 hover:text-gray-700"
              onClick={() => {
                setSelectedTicket(null);
                setTicketComments([]);
                setNewComment("");
              }}
            >
              Close
            </button>
          </div>

          {detailsError && (
            <div className="mt-3 p-3 rounded-md border border-red-200 bg-red-50 text-red-700">
              {detailsError}
            </div>
          )}

          {loadingDetails ? (
            <div className="mt-4 text-sm text-gray-500">Loading details…</div>
          ) : (
            <>
              <h5 className="mt-6 mb-2 font-medium text-gray-900">Comments</h5>
              <ul className="divide-y divide-gray-200 rounded border border-gray-200">
                {ticketComments.length === 0 && (
                  <li className="p-3 text-sm text-gray-500">
                    No comments yet.
                  </li>
                )}
                {ticketComments.map((c) => (
                  <li key={c.id} className="p-3">
                    <div className="text-sm text-gray-900">
                      {c.author}{" "}
                      <span className="text-xs text-gray-500">
                        ({c.authorType})
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap mt-1">
                      {c.message}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(c.createdAt).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add a comment
                </label>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Write your reply…"
                />
                <div className="mt-2">
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                    disabled={commentSubmitting || !newComment.trim()}
                    onClick={async () => {
                      if (!selectedTicket) return;
                      setCommentSubmitting(true);
                      try {
                        const resp = await realAPI.addMyTicketComment(
                          selectedTicket.id,
                          { message: newComment.trim() }
                        );
                        const created = resp?.data;
                        if (created)
                          // Append to keep oldest-first ordering
                          setTicketComments((prev) => [...prev, created]);
                        setNewComment("");
                      } catch (e: any) {
                        setDetailsError(e?.message || "Failed to post comment");
                      } finally {
                        setCommentSubmitting(false);
                      }
                    }}
                  >
                    {commentSubmitting ? "Posting…" : "Post Comment"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
